import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';

const Login = ({ onLogin, onVerifyMfa, mfaToken }) => {
  const [loading, setLoading] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const codeRef = useRef(null);

  const isMfaStep = Boolean(mfaToken);

  useEffect(() => {
    setTimeout(() => setAnimateIn(true), 100);
  }, []);

  useEffect(() => {
    if (isMfaStep && codeRef.current) {
      codeRef.current.focus();
    }
  }, [isMfaStep]);

  const handleSubmitCredentials = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Email and password are required');
      return;
    }
    setLoading(true);
    try {
      const result = await onLogin({ email, password });
      if (!result.success) toast.error(result.error);
    } catch {
      toast.error('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyMfa = async (e) => {
    e.preventDefault();
    if (!code || code.length !== 6) {
      toast.error('Enter the 6-digit code sent to your email');
      return;
    }
    setLoading(true);
    try {
      const result = await onVerifyMfa({ mfa_token: mfaToken, code });
      if (!result.success) toast.error(result.error);
    } catch {
      toast.error('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modern-login-container">
      <div className="login-background">
        <div className="floating-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
          <div className="shape shape-4"></div>
          <div className="shape shape-5"></div>
        </div>
      </div>

      <div className={`login-content ${animateIn ? 'animate-in' : ''}`}>
        <div className="login-card">
          <div className="login-header">
            <div className="logo-container">
              <div className="logo-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
                </svg>
              </div>
              <h1 className="login-title">Chatbot Admin</h1>
              <p className="login-subtitle">
                {isMfaStep ? 'Check your email for the login code' : 'Sign in to access your dashboard'}
              </p>
            </div>
          </div>

          {/* Step 1: Email + Password */}
          {!isMfaStep && (
            <form onSubmit={handleSubmitCredentials} className="login-form">
              <div className="form-group">
                <label className="form-label">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="input-icon">
                    <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                  </svg>
                  Email
                </label>
                <input
                  type="email"
                  className="form-input"
                  value={email}
                  onChange={e => setEmail(e.target.value.toLowerCase())}
                  placeholder="Enter your email"
                  autoComplete="email"
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="input-icon">
                    <path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                  </svg>
                  Password
                </label>
                <div className="password-input-container">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="form-input"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                  />
                  <button type="button" className="password-toggle" onClick={() => setShowPassword(!showPassword)}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      {showPassword
                        ? <path d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"/>
                        : <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8zM12 9a3 3 0 100 6 3 3 0 000-6z"/>
                      }
                    </svg>
                  </button>
                </div>
              </div>

              <button type="submit" className={`login-button ${loading ? 'loading' : ''}`} disabled={loading}>
                {loading ? <><div className="spinner"></div>Sending code...</> : <>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"/>
                  </svg>
                  Continue
                </>}
              </button>
            </form>
          )}

          {/* Step 2: OTP code */}
          {isMfaStep && (
            <form onSubmit={handleVerifyMfa} className="login-form">
              <div className="form-group">
                <label className="form-label">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="input-icon">
                    <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
                  </svg>
                  6-digit code
                </label>
                <input
                  ref={codeRef}
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  className="form-input"
                  value={code}
                  onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  style={{ letterSpacing: '0.4em', fontSize: '1.5rem', textAlign: 'center' }}
                />
                <div style={{ marginTop: '6px', fontSize: '13px', color: '#666' }}>
                  Code sent to your email. Valid for 10 minutes.
                </div>
              </div>

              <button type="submit" className={`login-button ${loading ? 'loading' : ''}`} disabled={loading}>
                {loading ? <><div className="spinner"></div>Verifying...</> : 'Verify & Sign In'}
              </button>
            </form>
          )}

          <div className="login-footer">
            <p>© 2024 Chatbot Admin. All rights reserved.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
